import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, ViewChild } from '@angular/core';
import { ChatChartSpec } from '../../models/chat.models';

interface PrimeNgChartData {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    borderColor?: string | string[];
    backgroundColor?: string | string[];
    fill?: boolean;
    tension?: number;
  }>;
}

const PALETTE = ['#00C48C', '#1A1A2E', '#F59E0B', '#3B82F6'];

@Component({
  selector: 'app-chat-mini-chart',
  templateUrl: './chat-mini-chart.component.html',
  styleUrls: ['./chat-mini-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMiniChartComponent implements OnChanges {
  @Input({ required: true }) spec!: string;
  @ViewChild('chartWrapper') chartWrapper?: ElementRef<HTMLDivElement>;

  chartType: string = 'bar';
  chartData: PrimeNgChartData | null = null;
  chartOptions: Record<string, unknown> = {};
  title = '';
  fallbackRaw: string | null = null;

  ngOnChanges(): void {
    try {
      const parsed = JSON.parse(this.spec) as ChatChartSpec;
      if (!parsed || !Array.isArray(parsed.series) || parsed.series.length === 0) {
        this.fallbackRaw = this.spec;
        return;
      }
      this.title = parsed.title || '';
      const type = parsed.type || 'bar';
      this.chartType = type;
      const isPie = type === 'pie' || type === 'doughnut';
      const labels = parsed.series[0].data.map((p) => p.x);

      this.chartData = {
        labels,
        datasets: parsed.series.map((s, i) => ({
          label: s.name,
          data: s.data.map((p) => p.y),
          borderColor: isPie ? '#fff' : PALETTE[i % PALETTE.length],
          backgroundColor: isPie
            ? labels.map((_, j) => PALETTE[j % PALETTE.length])
            : PALETTE[i % PALETTE.length],
          fill: false,
          tension: type === 'line' ? 0.25 : 0,
        })),
      };

      this.chartOptions = isPie
        ? {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: 'bottom', labels: { padding: 12 } },
              tooltip: { callbacks: { label: (ctx: any) => `${ctx.label}: ${ctx.raw}` } },
            },
          }
        : {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { position: 'bottom' },
              tooltip: { enabled: true, mode: 'index', intersect: false },
            },
            scales: {
              x: { title: { display: !!parsed.xLabel, text: parsed.xLabel } },
              y: { title: { display: !!parsed.yLabel, text: parsed.yLabel }, beginAtZero: true },
            },
          };
      this.fallbackRaw = null;
    } catch {
      this.fallbackRaw = this.spec;
    }
  }

  downloadPng(): void {
    const canvas = this.chartWrapper?.nativeElement?.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(this.title || 'grafica').replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.png`;
    a.click();
  }
}
